import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/hooks/useApi";
import type { AssistantSession, StreamMessage } from "../types";
import type { ThreadsStore } from "./threadsStore";
import { useLatestRef } from "@/hooks/useLatestRef";

/**
 * Messages per fetch, for both the initial windowed session snapshot and
 * each older page. The server clamps to 200; this is the single frontend
 * knob for how much transcript loads at a time.
 */
export const MESSAGE_PAGE_SIZE = 15;

/** Asks message reads for attachment URLs instead of inline base64. */
export const ATTACHMENTS_AS_URLS = "url";
interface EarlierPageResponse {
  session_id: string;
  messages: StreamMessage[];
  has_more: boolean;
}

/**
 * Paging state for one session's loaded older pages. Snapshot refetches keep
 * returning the windowed tail with `messages_has_more: true`, so once a page
 * has been loaded the client-side cursor (older than anything the snapshot
 * carries) is the truth, not the snapshot's flag.
 */
interface PagedState {
  /**
   * Bucket epoch the loaded pages were ingested into. An evicted bucket
   * rehydrates from the snapshot window only, dropping prepended rows, so a
   * changed epoch invalidates the cursor and falls back to the snapshot's.
   */
  bucketEpoch: number;
  cursorId: string | null;
  hasMore: boolean;
}

/**
 * Earlier-page loading for the windowed session snapshot: fetches the
 * page before the oldest loaded row and merges it into the thread store,
 * where rows prepend by sort order (`applySnapshot` merges by id and never
 * deletes, so later snapshot refetches keep the prepended rows).
 */
export function useEarlierMessages(sessionId, session, store) {
  const $ = _c(15);
  const api = useApi();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [pagedBySession, setPagedBySession] = useState(t0);
  const [loadingSessionIds, setLoadingSessionIds] = useState(_temp);
  const activeSessionIdRef = useLatestRef(sessionId);
  const snapshot = session && session.id === sessionId ? session : null;
  const snapshotIsComplete = snapshot?.messages !== undefined && snapshot.messages_has_more !== true;
  const snapshotFirstMessageId = snapshot?.messages?.[0]?.id ?? null;
  const bucketEpoch = sessionId !== null ? store.bucketEpoch(sessionId) : null;
  const paged = sessionId !== null ? pagedBySession[sessionId] ?? null : null;
  const exhaustedRecordCurrent = paged !== null && !paged.hasMore && (paged.bucketEpoch === bucketEpoch || paged.cursorId === snapshotFirstMessageId);
  if (sessionId !== null && bucketEpoch !== null && snapshotIsComplete && !exhaustedRecordCurrent) {
    setPagedBySession(prev => ({
      ...prev,
      [sessionId]: {
        bucketEpoch,
        cursorId: snapshotFirstMessageId,
        hasMore: false
      }
    }));
  }
  const pagedActive = paged !== null && paged.bucketEpoch === bucketEpoch ? paged : null;
  const hasEarlierMessages = pagedActive ? pagedActive.hasMore : snapshot?.messages_has_more ?? false;
  const cursorId = pagedActive ? pagedActive.cursorId : snapshot?.messages?.[0]?.id ?? null;
  let t1;
  if ($[1] !== loadingSessionIds || $[2] !== sessionId) {
    t1 = sessionId !== null && loadingSessionIds.has(sessionId);
    $[1] = loadingSessionIds;
    $[2] = sessionId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isLoadingEarlier = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const inFlightSessionIdsRef = useRef(t2);
  let t3;
  if ($[5] !== activeSessionIdRef || $[6] !== api || $[7] !== cursorId || $[8] !== sessionId || $[9] !== store) {
    t3 = async () => {
      if (!sessionId || !cursorId) {
        return;
      }
      if (inFlightSessionIdsRef.current.has(sessionId)) {
        return;
      }
      inFlightSessionIdsRef.current.add(sessionId);
      setLoadingSessionIds(prev_0 => new Set(prev_0).add(sessionId));
      await api.get(`/assistant/sessions/${sessionId}/messages`, {
        params: {
          before_message_id: cursorId,
          limit: MESSAGE_PAGE_SIZE,
          attachments: ATTACHMENTS_AS_URLS
        }
      }).then(res => {
        const page = res.data;
        if (activeSessionIdRef.current !== sessionId) {
          return;
        }
        if (page.messages.length > 0) {
          store.applySnapshot(sessionId, page.messages, undefined);
        }
        setPagedBySession(prev_1 => ({
          ...prev_1,
          [sessionId]: {
            bucketEpoch: store.bucketEpoch(sessionId),
            cursorId: page.messages[0]?.id ?? cursorId,
            hasMore: page.has_more
          }
        }));
      }).catch(_temp2).finally(() => {
        inFlightSessionIdsRef.current.delete(sessionId);
        setLoadingSessionIds(prev_2 => {
          const next = new Set(prev_2);
          next.delete(sessionId);
          return next;
        });
      });
    };
    $[5] = activeSessionIdRef;
    $[6] = api;
    $[7] = cursorId;
    $[8] = sessionId;
    $[9] = store;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const loadEarlierMessages = t3;
  let t4;
  if ($[11] !== hasEarlierMessages || $[12] !== isLoadingEarlier || $[13] !== loadEarlierMessages) {
    t4 = {
      hasEarlierMessages,
      isLoadingEarlier,
      loadEarlierMessages
    };
    $[11] = hasEarlierMessages;
    $[12] = isLoadingEarlier;
    $[13] = loadEarlierMessages;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function _temp2(error) {
  console.error("Failed to load earlier messages:", error);
  throw error;
}
function _temp() {
  return new Set();
}